/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * One definition of "which URLs are lowercase-canonical" (UCR-3053).
 *
 * Shared deliberately: the canonical-case middleware 301s toward this form, the sitemap must publish
 * exactly the form the middleware won't redirect, and the Learn route must build its canonical path
 * in this form or its 301 and the middleware's 301 will bounce a URL between them forever. Two copies
 * of this rule is one copy too many.
 *
 * Lowercase is canonical for root slugs (categories, landing pages) and `/learn/**`, because every
 * live category id and article slug already is.
 */

/**
 * Path families whose own casing is canonical, so the lowercase rule must not touch them. Matched on a
 * **segment boundary** — `/product` and `/product/…`, never `/productivity`.
 *
 * - `/product` — master ids are legitimately mixed-case (`Living-Soil-Kit`) and variant segments are
 *   SKUs that may contain uppercase (`25752235M`). Lowercasing would 404 every PDP. Casing for this
 *   family is fixed by the PDP loader instead, which 301s a wrong-cased id onto the catalog's casing.
 * - `/order-confirmation` — order numbers may contain uppercase.
 * - `/account` — authenticated pages; nothing indexable to canonicalize.
 * - `/action`, `/resource` — internal endpoints, not shopper-facing URLs.
 * - `/oauth2` — well-known paths whose casing is fixed by the spec/consumer.
 */
export const CASE_EXEMPT_PREFIXES = [
    '/product',
    '/account',
    '/order-confirmation',
    '/action',
    '/resource',
    '/oauth2',
] as const;

/** Is this path inside a family that keeps its own casing? Segment-boundary match. */
export function isCaseExemptPath(pathname: string): boolean {
    return CASE_EXEMPT_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

/**
 * Does the last segment look like a file (`/sitemap.xml`, `/favicon.ico`, `/images/Logo.SVG`)? Those
 * are assets or fixed-name routes rather than content URLs, so their casing is left alone.
 *
 * Callers handling requests must strip React Router's single-fetch `.data` suffix before asking, or
 * every client-side navigation looks like a file request.
 */
export function hasFileExtension(pathname: string): boolean {
    return pathname.slice(pathname.lastIndexOf('/') + 1).includes('.');
}

/**
 * The casing this path should be published and served at: lowercased, unless it belongs to an exempt
 * family or names a file. Idempotent, so it is safe to apply on both the request and the build side.
 */
export function toCanonicalCase(pathname: string): string {
    if (isCaseExemptPath(pathname) || hasFileExtension(pathname)) return pathname;
    return pathname.toLowerCase();
}

/** Does this path already have its canonical casing? */
export function hasCanonicalCase(pathname: string): boolean {
    return toCanonicalCase(pathname) === pathname;
}
